import { useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Settings, Eye, EyeOff, Shield, ShieldCheck } from 'lucide-react';

const FIELD_TYPES = ['text', 'email', 'tel', 'select', 'textarea'];

const AdminRegistrationSchemaPage = () => {
  const [schema, setSchema] = useState({ fields: [], info_box: '' });
  const [site, setSite] = useState({ show_login_button: true, show_register_button: true });
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, st, ap] = await Promise.all([
        api.get('/admin/registrations/form-schema'),
        api.get('/admin/registrations/site-settings'),
        api.get('/admin/registrations/approvers'),
      ]);
      setSchema(s.data); setSite(st.data); setApprovers(ap.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'تعذّر التحميل'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addField = () => setSchema(p => ({ ...p, fields: [...p.fields, { id: `field_${Date.now()}`, label: 'حقل جديد', type: 'text', required: false, step: 1, options: [] }] }));
  const updField = (i, k, v) => setSchema(p => ({ ...p, fields: p.fields.map((f, idx) => idx === i ? { ...f, [k]: v } : f) }));
  const delField = (i) => setSchema(p => ({ ...p, fields: p.fields.filter((_, idx) => idx !== i) }));
  const moveField = (i, dir) => setSchema(p => {
    const arr = [...p.fields]; const ni = i + dir;
    if (ni < 0 || ni >= arr.length) return p;
    [arr[i], arr[ni]] = [arr[ni], arr[i]];
    return { ...p, fields: arr };
  });

  const saveSchema = async () => {
    try { await api.put('/admin/registrations/form-schema', schema); toast.success('تم حفظ الاستمارة'); }
    catch (e) { toast.error(e.response?.data?.detail || 'فشل الحفظ'); }
  };

  const saveSite = async () => {
    try { await api.put('/admin/registrations/site-settings', site); toast.success('تم حفظ إعدادات الواجهة'); }
    catch (e) { toast.error(e.response?.data?.detail || 'فشل الحفظ'); }
  };

  const toggleApprover = async (rn, allow) => {
    try {
      await api.put(`/admin/registrations/approver/${rn}`, { can_approve_registrations: allow });
      toast.success('تم التحديث');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'فشل'); }
  };

  if (loading) return <div className="text-center p-10 text-gray-500" dir="rtl">جارٍ التحميل...</div>;

  return (
    <div dir="rtl" className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-schema-page">
      <h1 className="text-2xl font-bold flex items-center gap-2" style={{color:'#5D4037'}}><Settings className="w-7 h-7" style={{color:'#C19A6B'}} />إعدادات استمارة التسجيل</h1>

      {/* Site Settings */}
      <section className="bg-white rounded-xl shadow border p-5">
        <h2 className="font-bold mb-4 flex items-center gap-2"><Eye className="w-5 h-5" style={{color:'#C19A6B'}} />إعدادات صفحة الهبوط</h2>
        <div className="flex flex-wrap gap-4 items-center">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-login-btn">
            <input type="checkbox" checked={site.show_login_button} onChange={e => setSite(p => ({...p, show_login_button: e.target.checked}))} />
            <span>إظهار زر &quot;تسجيل الدخول&quot;</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-register-btn">
            <input type="checkbox" checked={site.show_register_button} onChange={e => setSite(p => ({...p, show_register_button: e.target.checked}))} />
            <span>إظهار زر &quot;التسجيل في المبادرة&quot;</span>
          </label>
          <button onClick={saveSite} className="mr-auto px-4 py-2 rounded-lg text-white font-bold flex items-center gap-2" style={{background:'#3A5F0B'}} data-testid="save-site-btn">
            <Save className="w-4 h-4" />حفظ
          </button>
        </div>
      </section>

      {/* Form Schema */}
      <section className="bg-white rounded-xl shadow border p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold flex items-center gap-2"><Settings className="w-5 h-5" style={{color:'#C19A6B'}} />حقول الاستمارة</h2>
          <div className="flex gap-2">
            <button onClick={addField} className="px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm flex items-center gap-1" data-testid="add-field-btn"><Plus className="w-4 h-4" />إضافة حقل</button>
            <button onClick={saveSchema} className="px-4 py-1.5 rounded-lg text-white font-bold flex items-center gap-1" style={{background:'#3A5F0B'}} data-testid="save-schema-btn"><Save className="w-4 h-4" />حفظ</button>
          </div>
        </div>

        <div className="space-y-2">
          {schema.fields.map((f, i) => (
            <div key={i} className="border rounded-lg p-3 bg-gray-50 grid grid-cols-2 md:grid-cols-6 gap-2 items-start" data-testid={`field-row-${i}`}>
              <input placeholder="id" value={f.id || ''} onChange={e => updField(i, 'id', e.target.value)} className="border rounded px-2 py-1 text-xs font-mono" />
              <input placeholder="label" value={f.label || ''} onChange={e => updField(i, 'label', e.target.value)} className="border rounded px-2 py-1 text-sm col-span-2" />
              <select value={f.type || 'text'} onChange={e => updField(i, 'type', e.target.value)} className="border rounded px-2 py-1 text-xs">
                {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={f.step || 1} onChange={e => updField(i, 'step', Number(e.target.value))} className="border rounded px-2 py-1 text-xs">
                <option value={1}>مرحلة 1</option><option value={2}>مرحلة 2</option><option value={3}>مرحلة 3</option>
              </select>
              <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!f.required} onChange={e => updField(i, 'required', e.target.checked)} />مطلوب</label>
              <input placeholder="placeholder" value={f.placeholder || ''} onChange={e => updField(i, 'placeholder', e.target.value)} className="border rounded px-2 py-1 text-sm col-span-2" />
              {f.type === 'select' && (
                <input placeholder="خيارات مفصولة بفاصلة" value={(f.options || []).join(',')} onChange={e => updField(i, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className="border rounded px-2 py-1 text-sm col-span-3" />
              )}
              <div className="flex gap-1 col-span-1 justify-end">
                <button onClick={() => moveField(i, -1)} className="p-1 text-gray-500 hover:bg-gray-200 rounded"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => moveField(i, 1)} className="p-1 text-gray-500 hover:bg-gray-200 rounded"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => delField(i)} className="p-1 text-red-600 hover:bg-red-100 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5">
          <label className="block text-sm font-bold mb-2" style={{color:'#5D4037'}}>نص الصندوق التعريفي (المرحلة 3)</label>
          <textarea value={schema.info_box || ''} onChange={e => setSchema(p => ({...p, info_box: e.target.value}))} rows={6} className="w-full border rounded-lg px-3 py-2 text-sm font-sans" data-testid="info-box-textarea" />
        </div>
      </section>

      {/* Approvers */}
      <section className="bg-white rounded-xl shadow border p-5">
        <h2 className="font-bold mb-4 flex items-center gap-2"><Shield className="w-5 h-5" style={{color:'#C19A6B'}} />صلاحية الاعتماد للحسابات الإدارية</h2>
        <p className="text-xs text-gray-500 mb-4">يمكنك منح أو سحب صلاحية اعتماد طلبات التسجيل لأي حساب إداري. المدير المطلق يملك الصلاحية دائماً.</p>
        <div className="space-y-2">
          {approvers.map(a => {
            const isMaster = (a.admin_role || '').trim() === 'مطلق';
            return (
              <div key={a.reader_number} className="flex items-center justify-between border rounded-lg p-3 bg-gray-50">
                <div>
                  <p className="font-bold text-sm">{a.name || a.reader_number}</p>
                  <p className="text-xs text-gray-500">{a.reader_number} · {a.admin_role}</p>
                </div>
                {isMaster ? (
                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />مطلق - دائماً</span>
                ) : (
                  <label className="flex items-center gap-2 cursor-pointer" data-testid={`approver-${a.reader_number}`}>
                    <input type="checkbox" checked={a.can_approve_registrations} onChange={e => toggleApprover(a.reader_number, e.target.checked)} />
                    <span className="text-sm">يستطيع الاعتماد</span>
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default AdminRegistrationSchemaPage;
